import React, { FC, useEffect, useRef, useState } from 'react';

import { EnvironmentRead } from '@ariksa/compliance-policies';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { each, toArray } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { StackedHeader, WithResourceIcon } from 'components/DataDisplay';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { DashboardWidgetTooltipIcon } from 'containers/Dashboard/utils/utils';
import {
  renderBusinessCritical,
  renderClouds,
} from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ManagedEnvironment: FC<Props> = props => {
  const { isOpen, onClose } = props;
  const { blueprint } = useSelector(selectBlueprints);

  const ref = useRef(document.querySelector('.portal-container'));
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping } = useCloudAccountId();
  const [data, setData] = useState<EnvironmentRead[]>([]);
  const { setLearnMore } = useKnowledgeBase();

  useEffect(() => {
    let filteredEnvs: EnvironmentRead[] = [];
    each(blueprint.data?.environments, o => {
      let env = environmentMapping?.[o];
      if (!!env) filteredEnvs.push(env);
    });
    setData(filteredEnvs);
  }, [blueprint.data, environmentMapping]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Environment</Box>,
      accessor: 'name',
      render: ({ value }) => {
        return (
          <WithResourceIcon
            resourceType={IconTypes.Environment}
            color={customTheme.colors.orange['500']}
            useCustomColor
          >
            {value}
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Cloud',
      accessor: 'account_ids',
      align: 'left',
      render: ({ value }) => {
        return renderClouds(toArray(value), 'clouds', accountMapping);
      },
    },
    {
      header: 'Accounts',
      accessor: 'account_ids',
      render: ({ value }) => {
        return (
          <Center>
            {renderClouds(toArray(value), 'accounts', accountMapping)}
          </Center>
        );
      },
    },
    {
      header: 'Business Critical',
      accessor: 'is_business_critical',
      align: 'center',
      render: ({ value }) => renderBusinessCritical(value),
    },
  ];

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <HStack w="full" justify="space-between">
            <StackedHeader
              upper="Managed Environments"
              lower={blueprint.data?.name}
              type={IconTypes.Environment}
            />
            <Box pr={4}>
              <LearnMoreButton
                onOpen={() => setLearnMore('environments')}
                label="Environments"
              />
            </Box>
          </HStack>
        }
        styles={{
          content: { maxW: '1400px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
        closeButton
        body={
          <Stack w="full" h="full" pt={3} spacing={5}>
            <HStack>
              <DashboardWidgetTooltipIcon />
              <Box color="primary">
                To enforce policies on an environment, choose “Enforce” from the
                policy blueprint
              </Box>
            </HStack>
            <Box h="full">
              <Table
                data={data}
                columns={columns}
                cursor="pointer"
                /*onRowClick={row => {
                  dispatch(
                    actions.setAccount({
                      type: EnvironmentTypes.Environment,
                      environmentId: row?.id,
                      environment: row,
                    }),
                  );
                  navigate('/compliance');
                }}*/
              />
            </Box>
          </Stack>
        }
      />
    </>
  );
};
